/**
 * GigaLegal Design System Tokens
 * Single source of truth for design primitives used by Chakra + CSS utilities.
 */

export const colors = {
  base: {
    white: "#FFFFFF",
    black: "#000000",
    dark: "#0D0D0E",
  },
  brand: {
    primary: "#42E3B4",
    primaryHover: "#5FE8C0",
    primaryActive: "#2E9F82",
    secondary: "#24262B",
    periwinkle: "#9F7CFF",
    maroon: "#B2455C",
  },
  neutral: {
    dark: "#0D0D0E",
    card: "#24262B",
    cardHover: "#2A2C33",
    elevated: "#2A2C33",
  },
  text: {
    primary: "#FFFFFF",
    secondary: "#EDEDED",
    tertiary: "rgba(237, 237, 237, 0.75)",
    muted: "rgba(237, 237, 237, 0.7)",
    inverted: "#0D0D0E",
    disabled: "rgba(255, 255, 255, 0.3)",
  },
  background: {
    primary: "#0D0D0E",
    card: "#24262B",
    elevated: "#2A2C33",
    darkPrimary: "#090B0F",
    darkSecondary: "#12141A",
    overlay: "rgba(13, 13, 14, 0.8)",
  },
  border: {
    default: "rgba(255, 255, 255, 0.1)",
    light: "rgba(255, 255, 255, 0.12)",
    subtle: "rgba(255, 255, 255, 0.05)",
    active: "#42E3B4",
    card: "rgba(255, 255, 255, 0.08)",
  },
  state: {
    hover: "rgba(66, 227, 180, 0.08)",
    active: "rgba(66, 227, 180, 0.15)",
    disabled: "rgba(255, 255, 255, 0.2)",
    focus: "rgba(66, 227, 180, 0.25)",
  },
  blur: {
    dark: "rgba(255, 255, 255, 0.06)",
    medium: "rgba(255, 255, 255, 0.08)",
    light: "rgba(255, 255, 255, 0.14)",
  },
  success: {
    50: "#E6F9F3",
    100: "#CCF3E7",
    200: "#99E7CF",
    300: "#66DBB7",
    400: "#33CF9F",
    500: "#42E3B4",
    600: "#35B690",
    700: "#288A6C",
    800: "#1A5D48",
    900: "#0D2F24",
  },
  error: {
    50: "#FEF2F2",
    100: "#FEE2E2",
    200: "#FECACA",
    300: "#FCA5A5",
    400: "#F87171",
    500: "#EF4444",
    600: "#DC2626",
    700: "#B91C1C",
    800: "#991B1B",
    900: "#7F1D1D",
  },
  warning: {
    50: "#FFFBEB",
    100: "#FEF3C7",
    200: "#FDE68A",
    300: "#FCD34D",
    400: "#FBBF24",
    500: "#F59E0B",
    600: "#D97706",
    700: "#B45309",
    800: "#92400E",
    900: "#78350F",
  },
};

export const effects = {
  glow: {
    primary: "rgba(66, 227, 180, 0.3)",
    primaryStrong: "rgba(66, 227, 180, 0.5)",
    card: "0 4px 20px rgba(0, 0, 0, 0.3)",
    cardHover: "0 8px 30px rgba(0, 0, 0, 0.4)",
    glowSubtle: "0 0 16px rgba(66, 227, 180, 0.18)",
  },
  gradient: {
    primary: "linear-gradient(135deg, #42E3B4 0%, #2E9F82 100%)",
    scrollbar: "linear-gradient(180deg, #42E3B4, #2E9F82)",
    text: "linear-gradient(135deg, #42E3B4 0%, #2E9F82 100%)",
  },
};

export const gradients = {
  midnightMesh: "radial-gradient(circle at center, rgba(66, 227, 180, 0.12), transparent 40%), linear-gradient(135deg, rgba(10, 12, 16, 0.9), rgba(20, 24, 32, 0.95))",
  aurora: "radial-gradient(circle at top left, rgba(66, 227, 180, 0.18), transparent 25%), radial-gradient(circle at bottom right, rgba(138, 43, 226, 0.18), transparent 30%)",
};

export const blur = {
  dark: "rgba(255, 255, 255, 0.06)",
  medium: "rgba(255, 255, 255, 0.08)",
  light: "rgba(255, 255, 255, 0.14)",
  strength: {
    default: "18px",
    heavy: "28px",
  },
};

export const typography = {
  fontFamily: {
    display: "'SBSansDisplay-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    body: "'SBSansText-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fallback: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    ui: "'SBSansText-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    large: { fontSize: "32px", lineHeight: "40px", fontWeight: 600, fontFamily: "'SBSansDisplay-Regular', sans-serif", letterSpacing: "-0.02em" },
    medium: { fontSize: "24px", lineHeight: "32px", fontWeight: 600, fontFamily: "'SBSansDisplay-Regular', sans-serif", letterSpacing: "-0.01em" },
    md: { fontSize: "24px", lineHeight: "32px", fontWeight: 600, fontFamily: "'SBSansDisplay-Regular', sans-serif", letterSpacing: "-0.01em" },
    small: { fontSize: "20px", lineHeight: "28px", fontWeight: 600, fontFamily: "'SBSansDisplay-Regular', sans-serif" },
  },
  cardTitle: {
    large: { fontSize: "20px", lineHeight: "28px", fontWeight: 600, fontFamily: "Arial, sans-serif" },
    medium: { fontSize: "16px", lineHeight: "24px", fontWeight: 600, fontFamily: "Arial, sans-serif" },
  },
  body: {
    large: { fontSize: "20px", lineHeight: "30px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
    medium: { fontSize: "16px", lineHeight: "24px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
    small: { fontSize: "14px", lineHeight: "22px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
    sm: { fontSize: "14px", lineHeight: "22px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
    md: { fontSize: "16px", lineHeight: "24px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
    lg: { fontSize: "20px", lineHeight: "30px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
  },
  button: { fontSize: "14px", lineHeight: "20px", fontWeight: 600, fontFamily: "'SBSansDisplay-Regular', sans-serif" },
  meta: {
    sm: { fontSize: "12px", lineHeight: "18px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
  },
  footnote: { fontSize: "12px", lineHeight: "18px", fontWeight: 400, fontFamily: "'SBSansText-Regular', sans-serif" },
};

const chakraSpacingScale = [
  "0rem",
  "0.25rem",
  "0.5rem",
  "0.75rem",
  "1rem",
  "1.25rem",
  "1.5rem",
  "1.75rem",
  "2rem",
  "2.5rem",
  "3rem",
  "4rem",
  "5rem",
  "6rem",
  "7.5rem",
];

const spacingAliases = {
  none: 0,
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
  "2xl": 6,
  "3xl": 7,
  "4xl": 8,
  "5xl": 9,
  "6xl": 10,
  "7xl": 11,
  "8xl": 12,
};

const spacingPxAliases = {
  none: "0px",
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  "2xl": "24px",
  "3xl": "28px",
  "4xl": "32px",
  "5xl": "40px",
  "6xl": "48px",
  "7xl": "64px",
  "8xl": "80px",
};

export const spacing = Object.assign([...chakraSpacingScale], {
  scale: chakraSpacingScale,
  px: spacingPxAliases,
  ...spacingAliases,
});

export const borderRadius = {
  sm: "8px",
  md: "16px",
  lg: "20px",
  xl: "24px",
  full: "9999px",
  button: "8px",
  card: "24px",
  image: "20px",
};

export const borderWidth = { default: "1px", medium: "2px", thick: "3px" };

export const shadows = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.05)",
  md: "0 4px 6px rgba(0, 0, 0, 0.1)",
  lg: "0 10px 15px rgba(0, 0, 0, 0.2)",
  xl: "0 20px 25px rgba(0, 0, 0, 0.25)",
  card: "0 4px 20px rgba(0, 0, 0, 0.3)",
  cardHover: "0 8px 30px rgba(0, 0, 0, 0.4)",
  glow: "0 0 20px rgba(66, 227, 180, 0.3)",
  glowStrong: "0 0 40px rgba(66, 227, 180, 0.5)",
  subtle: "0 1px 3px rgba(0, 0, 0, 0.1)",
};

export const transitions = {
  default: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  smooth: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  fast: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  duration: { short: 0.15, medium: 0.2, long: 0.3, verySlow: 0.5 },
  easing: {
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

export const animations = {
  float: `@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }`,
  pulseGlow: `@keyframes pulseGlow { 0%, 100% { opacity: 0.4; box-shadow: 0 0 20px rgba(66, 227, 180, 0.3); } 50% { opacity: 0.8; box-shadow: 0 0 40px rgba(66, 227, 180, 0.5); } }`,
  fadeInUp: `@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`,
  borderGlow: `@keyframes borderGlow { 0%, 100% { border-color: rgba(66, 227, 180, 0.3); box-shadow: 0 0 10px rgba(66, 227, 180, 0.2); } 50% { border-color: rgba(66, 227, 180, 0.6); box-shadow: 0 0 20px rgba(66, 227, 180, 0.4); } }`,
};

export const breakpoints = { mobile: "320px", tablet: "768px", desktop: "1024px", wide: "1280px", "2xl": "1440px" };

export const containers = { maxWidth: "1200px", padding: { mobile: "16px", desktop: "20px", wide: "244.67px" } };

export const dimensions = {
  header: { height: "72px" },
  button: { height: "38px", minWidth: "80px" },
  icon: { sm: "16px", md: "20px", lg: "24px", xl: "32px" },
  card: { minHeight: "200px" },
};

export const tokens = {
  colors,
  effects,
  typography,
  spacing,
  borderRadius,
  borderWidth,
  shadows,
  transitions,
  animations,
  breakpoints,
  containers,
  dimensions,
};
